import { useEffect, useMemo, useRef, useState } from 'react';
import type { TonyteActivity } from '../types';
import { selectWeeklyFeatured } from '../utils/weeklyFeatured';

const RECHECK_INTERVAL_MS = 60 * 1000;

function sameEvents(a: TonyteActivity[], b: TonyteActivity[]): boolean {
  return a.length === b.length && a.every((activity, index) => activity.id === b[index].id);
}

// Active "Featured This Week" events for the selected city, derived from the
// already-loaded feed pool (no Firestore reads of its own).
//
// The featured window and event end times are wall-clock facts, so a screen
// left open across one must drop the event without a manual refresh. A cheap
// once-a-minute check re-evaluates the list but only triggers a re-render when
// the visible set actually changes.
export function useWeeklyFeatured(activities: TonyteActivity[], cityKey: string | null): TonyteActivity[] {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const featured = useMemo(
    () => selectWeeklyFeatured(activities, { now: new Date(nowMs), cityKey }),
    [activities, cityKey, nowMs],
  );

  const latest = useRef({ activities, cityKey, featured });
  latest.current = { activities, cityKey, featured };

  useEffect(() => {
    const timer = setInterval(() => {
      const current = latest.current;
      const now = Date.now();
      const next = selectWeeklyFeatured(current.activities, { now: new Date(now), cityKey: current.cityKey });
      if (!sameEvents(next, current.featured)) setNowMs(now);
    }, RECHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  return featured;
}
